/**
 * Responsive Layout Utilities
 * 
 * Per-field override system for orientation-specific sprite properties.
 * 
 * Rules:
 * - A sprite has a single `basis` orientation (where it was first created).
 * - In the basis orientation, values are the source of truth for all fields.
 * - In non-basis orientations, only explicitly overridden fields are stored.
 *   Non-overridden fields fall back to the basis value.
 * - Each field (posX, posY, scale, rotation) is independently overridable.
 * - Override badges appear per field; removal deletes only that field.
 */

import type { 
  Position, 
  Transform, 
  Orientation, 
  OrientationLayout,
  ResponsiveLayout 
} from '../../../Shared/src/@types/scene';
import {
  getBasisOrientation,
  getResponsivePosition,
  getResponsiveTransform,
  setPositionField,
  setOrientationTransform,
  removeFieldOverride,
  isDerived,
  withDerived,
  setAtRestFlip,
  type OverridableField,
} from '../../../Shared/src/utils/responsiveLayout';

// The resolvers live in Shared (spec 012) so the player resolves layouts
// exactly as the editor does; the per-field writes live there too (spec 021)
// so the scene operations write exactly what the editor writes.
export { getBasisOrientation, getResponsivePosition, getResponsiveTransform };
export { setPositionField, setOrientationTransform, removeFieldOverride, isDerived, withDerived, setAtRestFlip };
export type { OverridableField };

/**
 * Default position when none is specified
 */
const DEFAULT_POSITION: Position = {
  anchor: 'center',
  x: 50,
  y: 50,
  unit: '%',
};


/**
 * Get the opposite orientation (legacy helper).
 */
export function getOtherOrientation(orientation: Orientation): Orientation {
  if (orientation === 'portrait') return 'landscape';
  if (orientation === 'landscape') return 'portrait';
  return 'landscape'; // desktop falls back to landscape
}

// ---------------------------------------------------------------------------
// Basis helpers
// ---------------------------------------------------------------------------

/**
 * Get the basis orientation's layout, falling back to legacy scan if no basis set.
 */
function getBasisLayout(layout: ResponsiveLayout): OrientationLayout | undefined {
  if (layout.basis) {
    return layout[layout.basis];
  }
  // Legacy fallback: find the first orientation with position
  for (const o of ['portrait', 'landscape', 'desktop'] as Orientation[]) {
    if (layout[o]?.position) return layout[o];
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Position getters
// ---------------------------------------------------------------------------

/**
 * Get the layout for a specific orientation with fallback to basis.
 */
export function getOrientationLayout(
  layout: ResponsiveLayout,
  orientation: Orientation
): OrientationLayout | undefined {
  const currentLayout = layout[orientation];
  if (currentLayout?.position) return currentLayout;
  return getBasisLayout(layout);
}

// ---------------------------------------------------------------------------
// Transform getters
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Visibility
// ---------------------------------------------------------------------------

/**
 * Check if element is visible in the given orientation.
 */
export function isVisibleInOrientation(
  layout: ResponsiveLayout,
  orientation: Orientation
): boolean {
  const currentLayout = layout[orientation];
  if (currentLayout?.visible !== undefined) return currentLayout.visible;
  if (currentLayout?.position) return true;

  const otherOrientation = getOtherOrientation(orientation);
  const otherLayout = layout[otherOrientation];
  if (otherLayout?.visible !== undefined) return otherLayout.visible;

  return !!(currentLayout?.position || otherLayout?.position);
}

// ---------------------------------------------------------------------------
// Position setters
// ---------------------------------------------------------------------------

/**
 * Set a full position for an orientation.
 * Sets basis on first write. The position is authored, so the `position`
 * part of the spec 021 derived mark is dropped (as `setPositionField` does).
 */
export function setOrientationPosition(
  layout: ResponsiveLayout,
  orientation: Orientation,
  position: Position
): ResponsiveLayout {
  const basis = getBasisOrientation(layout) ?? orientation;
  const entry = withDerived(layout[orientation] ?? ({} as Partial<OrientationLayout>), 'position', false);
  return {
    ...layout,
    basis,
    [orientation]: {
      ...entry,
      position,
    },
  };
}

// ---------------------------------------------------------------------------
// Has/check helpers
// ---------------------------------------------------------------------------

/**
 * Check if the layout has a position defined for the given orientation.
 */
export function hasOrientationPosition(
  layout: ResponsiveLayout,
  orientation: Orientation
): boolean {
  return !!layout[orientation]?.position;
}

/**
 * Check if the layout has a transform defined for the given orientation.
 */
export function hasOrientationTransform(
  layout: ResponsiveLayout,
  orientation: Orientation
): boolean {
  return !!layout[orientation]?.transform;
}

// ---------------------------------------------------------------------------
// Per-field override detection
// ---------------------------------------------------------------------------

/**
 * Check if a specific field is overridden for this orientation.
 * The basis orientation never has overrides.
 * A non-basis orientation has an override if that field is explicitly stored.
 */
export function isFieldOverride(
  layout: ResponsiveLayout,
  orientation: Orientation,
  field: OverridableField
): boolean {
  const basis = getBasisOrientation(layout);
  if (!basis || orientation === basis) return false;

  const oriLayout = layout[orientation];
  if (!oriLayout) return false;

  if (field === 'x' || field === 'y') {
    return oriLayout.position != null && field in oriLayout.position;
  }
  if (field === 'scale' || field === 'rotation') {
    return oriLayout.transform != null && field in oriLayout.transform;
  }
  return false;
}

/**
 * Legacy: check if position or transform as a whole is an override.
 * Returns true if ANY sub-field is overridden.
 */
export function isOrientationOverride(
  layout: ResponsiveLayout,
  orientation: Orientation,
  property: 'position' | 'transform'
): boolean {
  if (property === 'position') {
    return isFieldOverride(layout, orientation, 'x') ||
           isFieldOverride(layout, orientation, 'y');
  }
  return isFieldOverride(layout, orientation, 'scale') ||
         isFieldOverride(layout, orientation, 'rotation');
}

// ---------------------------------------------------------------------------
// Per-field override removal
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Whole-orientation override removal (legacy wrappers)
// ---------------------------------------------------------------------------

/**
 * Remove all position overrides for a non-basis orientation.
 */
export function removeOrientationPosition(
  layout: ResponsiveLayout,
  orientation: Orientation
): ResponsiveLayout {
  const basis = getBasisOrientation(layout);
  if (orientation === basis) return layout;
  const oriLayout = layout[orientation];
  if (!oriLayout?.position) return layout;
  const { position: _removed, ...rest } = oriLayout;
  const hasRest = Object.keys(rest).length > 0;
  return { ...layout, [orientation]: hasRest ? rest : undefined };
}

/**
 * Remove all transform overrides for a non-basis orientation.
 */
export function removeOrientationTransform(
  layout: ResponsiveLayout,
  orientation: Orientation
): ResponsiveLayout {
  const basis = getBasisOrientation(layout);
  if (orientation === basis) return layout;
  const oriLayout = layout[orientation];
  if (!oriLayout?.transform) return layout;
  const { transform: _removed, ...rest } = oriLayout;
  const hasRest = Object.keys(rest).length > 0;
  return { ...layout, [orientation]: hasRest ? rest : undefined };
}

// ---------------------------------------------------------------------------
// Layout constructors
// ---------------------------------------------------------------------------

/**
 * Create a new ResponsiveLayout with initial position for one orientation.
 */
export function createResponsiveLayout(
  orientation: Orientation,
  position: Position,
  transform?: Transform
): ResponsiveLayout {
  return {
    basis: orientation,
    [orientation]: {
      position,
      transform,
    },
  };
}

/**
 * Create a default ResponsiveLayout centered in both orientations.
 */
export function createDefaultLayout(): ResponsiveLayout {
  return {
    basis: 'portrait',
    portrait: {
      position: { ...DEFAULT_POSITION },
    },
  };
}
